import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, User, Send, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TYPES = [
  { id: "csr", label: "CSR / Company team" },
  { id: "individual", label: "Individual donor" },
  { id: "ngo", label: "NGO" },
  { id: "other", label: "Other" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", org_type: "csr", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="bg-slate-brand text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
          <h1 className="text-3xl sm:text-4xl font-extrabold">Talk to a human</h1>
          <p className="text-slate-300 mt-3 measure text-lg">CSR team shortlisting partners, an individual who wants to give, or an NGO wanting to be listed — tell us what you need and we'll connect you directly. No account required, no cost, no money ever routed through us.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-3 gap-8">
        <aside className="space-y-4 text-sm text-stone-700">
          <div className="flex items-start gap-2"><Mail size={16} className="text-terracotta mt-0.5" /> <div><div className="font-semibold text-slate-brand">Email</div><a href="mailto:connect@thadam.org" className="text-terracotta">connect@thadam.org</a></div></div>
          <div className="flex items-start gap-2"><Building2 size={16} className="text-terracotta mt-0.5" /> <div><div className="font-semibold text-slate-brand">Office</div>Coimbatore &amp; Erode, Tamil Nadu</div></div>
          <div className="rounded-xl border border-stone-200 bg-white p-4 text-xs text-stone-600">We reply to every genuine enquiry within 3 working days. For grievances, our DPO resolves within 90 days.</div>
        </aside>

        <div className="md:col-span-2">
          {sent ? (
            <div className="rounded-2xl border border-tier3-border bg-tier3-bg p-8 text-center" data-testid="enquiry-success">
              <CheckCircle2 className="mx-auto text-tier3-icon" size={40} />
              <h2 className="text-xl font-bold text-slate-brand mt-3">Enquiry received</h2>
              <p className="text-stone-600 mt-1">We have your details and will reach out directly. Meanwhile, feel free to browse verified NGOs.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
              <div>
                <Label className="text-sm mb-2 block">I am a…</Label>
                <div className="grid grid-cols-2 gap-2">
                  {TYPES.map((tp) => (
                    <button type="button" key={tp.id} onClick={() => set("org_type", tp.id)} data-testid={`enquiry-type-${tp.id}`}
                      className={`px-3 py-2 rounded-lg border text-sm text-left ${form.org_type === tp.id ? "border-terracotta bg-terracotta-light text-slate-brand font-semibold" : "border-stone-200 text-stone-600"}`}>{tp.label}</button>
                  ))}
                </div>
              </div>
              <div><Label htmlFor="n">Name</Label><Input id="n" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="enquiry-name" className="mt-1.5" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="e">Email</Label><Input id="e" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="enquiry-email" className="mt-1.5" /></div>
                <div><Label htmlFor="p">Phone (optional)</Label><Input id="p" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquiry-phone" className="mt-1.5" /></div>
              </div>
              <div><Label htmlFor="m">What do you need?</Label><Textarea id="m" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} required placeholder="e.g. We have a ₹40L CSR budget for water conservation in Coimbatore and need 3 compliant partners." data-testid="enquiry-message" className="mt-1.5" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-hover" data-testid="enquiry-submit">
                <Send size={16} className="mr-1.5" /> {loading ? "Sending…" : "Send enquiry"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
